<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';
    // Sites Store
    const { sitesData } = storeToRefs(useArchivesStore());
    const { neighborhoodsMap } = storeToRefs(useArchivesNeighborhoodsStore());

    // Properties
    const showFilters = ref(false);
    // Filters/Sorts
    const searchTerm = ref('');
    const filters = shallowRef(false);
    const showSearchLabel = ref(true);
    const sitesSource = shallowRef(sitesData.value);
    const props = defineProps({
        year: {
            type: String,
            required: false,
        },
    });
    // Computed
    const filteredSites = computed(() => {
        let filtered = sitesSource.value;
        if (searchTerm.value && searchTerm.value.length) {
            filtered = filtered.filter((el) => {
                return el.fields.Name.toLowerCase().includes(searchTerm.value.toLowerCase());
            });
        }
        if (props.year) {
            filtered = filtered.filter((el) => {
                if (el.fields['OHC Years of Participation']) {
                    return el.fields['OHC Years of Participation'].includes(props.year);
                }
                return false;
            });
        }
        return filtered;
    });

    const title = computed(() => {
        return props.year ? `${props.year} OHC Site Archives` : 'OHC Site Archives';
    });

    const check_for_empty = (reactive_array) => {
        return Object.values(reactive_array).every((value) => value.length === 0 || !value);
    };

    const smart_join = (arr) => {
        if (arr.length === 0) return '';
        if (arr.length === 1) return arr[0];
        if (arr.length === 2) return arr.join(' and ');
        return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
    };

    const filtersTitle = computed(() => {
        const noun = filteredSites.value.length === 1 ? 'site' : 'sites';
        if (!searchTerm.value && (!filters.value || check_for_empty(filters.value))) {
            return `Showing all ${filteredSites.value.length} ${noun}`;
        }
        const label = [`Showing ${filteredSites.value.length} ${noun}`];
        if (filters.value.hoodFilters && neighborhoodsMap.value && neighborhoodsMap.value[filters.value.hoodFilters]) {
            label.push('in ' + neighborhoodsMap.value[filters.value.hoodFilters].fields['Name']);
        }
        if (filters.value.yearsFilters?.length && filters.value.yearsFilters.length !== 0) {
            label.push('from ' + smart_join(filters.value.yearsFilters.sort((a, b) => b - a)));
        }
        if (searchTerm.value && searchTerm.value.length) {
            label.push('matching "' + searchTerm.value + '"');
        }
        return label.join(' ');
    });

    // Methods
    function toggleSearchLabel(val) {
        if (val === 'hide') {
            showSearchLabel.value = false;
        } else {
            showSearchLabel.value = true;
        }
    }


    const searchInputId = useId();

    function toggleFilters() {
        if (searchTerm.value !== '') {
            searchTerm.value = '';
        }
        showFilters.value = !showFilters.value;
    }

    function updateSites(val) {
        sitesSource.value = val;
        showFilters.value = false;
    }

    function updateFilters(val) {
        filters.value = unref(val);
    }

    function resetSites() {
        sitesSource.value = sitesData.value;
    }

    // Lifecycle
    onMounted(() => {
        if (import.meta.client) {
            showFilters.value = false;
        }
    });

    watch(sitesData, () => {
        sitesSource.value = sitesData.value;
    });
</script>

<template>
    <section class="archives-overview">
        <template v-if="sitesData && sitesData.length > 0">
            <GlobalFlyout v-show="showFilters" @toggleFilters="toggleFilters" :showing="showFilters" v-if="!year">
                <FiltersArchives :sites="sitesData" @close="toggleFilters" @updateSites="updateSites" @resetSites="resetSites" @updateFilters="updateFilters" />
            </GlobalFlyout>
            <div id="content-wrapper">
                <BlocksTitleListToggle :title="title" :subtitle="filtersTitle" v-show="!showFilters" />
                <div class="container hd-container" v-if="!year">
                    <div class="filters">
                        <button v-if="!showFilters" type="button" class="show-filters" @click="toggleFilters" ref="filterToggle" aria-label="Filters">
                            <SvgsFilters class="filter-icon" />
                        </button>
                        <button v-if="!showFilters" type="button" class="desktop-filters button black" @click="toggleFilters" ref="filterToggle">Filters</button>
                        <div class="form-field search" role="search" v-show="!showFilters">
                            <label :for="searchInputId" :class="{ 'sr-only': !showSearchLabel || searchTerm !== '' }">Search sites</label>
                            <SvgsSearch />
                            <input :id="searchInputId" type="search" v-model="searchTerm" @focus="toggleSearchLabel('hide')" @blur="toggleSearchLabel('show')" />
                        </div>
                    </div>
                </div>
                <div class="filter-stat" v-if="filteredSites.length > 0">
                    <h2 class="headline-2">{{ filtersTitle }}</h2>
                </div>
                <div class="hd-container">
                    <BlocksArchivesListStack :showHeader="false" :cards="filteredSites" :year="year" />
                </div>
                <div class="container" v-if="filteredSites.length === 0">
                    <p class="no-results subhead-2">No sites match these filters.</p>
                </div>
            </div>
        </template>
    </section>
</template>

<style lang="scss">
    .archives-overview {
        position: relative;

        .filters {
            display: flex;
            margin-bottom: 18px;

            @media (min-width: $tablet) {
                width: 100%;
            }

            .filter-icon {
                flex-shrink: 0;
                margin-right: 16px;
            }

            .select {
                label {
                    display: block;
                    margin-bottom: 16px;
                }
            }
        }

        .filter-stat {
            padding: 16px;
        }

        .map-sites-carousel {
            .component-header {
                display: none;
            }
            .carousel-component .carousel-inner {
                padding-top: 4px;
                padding-bottom: 4px;
            }
        }

        .desktop-filters {
            display: none;
        }

        .no-results {
            margin-top: 32px;
        }

        .title-map-list-toggle.component-header {
            padding: 20px 16px;
            
            @media (min-width: $desktop) {
                padding: 20px 0px;
            }
        }

        .sites-list-stack {
            padding-top: 0;
        }

        .carousel-component .carousel-inner .carousel-item.is-full-width {
            align-self: flex-end;
        }

        @media (min-width: $tablet) {
            max-width: $hd;
            width: 100vw;
            margin: auto;
            min-height: 60vh;

            .carousel-component .carousel-inner .carousel-item.is-full-width {
                align-self: stretch;
            }

            .show-filters {
                display: none;
            }

            .desktop-filters {
                display: block;
                width: auto;
                padding: 10px 70px;
                margin-right: 16px;
            }
        }
    }
</style>
